import Checkbox from "@mui/material/Checkbox";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import type { MasterCompanyListItem } from "../../../api/client.ts";
import CountryName from "../../../components/CountryName.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import SortableTableHead from "../../../components/table/SortableTableHead.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";
import { formatBucketLabel } from "../utils.ts";

interface ColumnDef {
  id: string | null;
  label: string;
  align?: "right";
  /** Label gets the display currency. */
  money?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { id: "name", label: "Name" },
  { id: "country", label: "Country" },
  { id: null, label: "Org number" },
  { id: "industry", label: "Industry" },
  { id: "org_form", label: "Org form" },
  { id: "employee_count", label: "Employees", align: "right" },
  { id: "revenue", label: "Revenue", align: "right", money: true },
  { id: "operating_result", label: "Operating result", align: "right", money: true },
];

/** A figure converted to `currency`, with the filed one on hover when it differs. */
function MoneyCell({
  value,
  reported,
  reportedCurrency,
  fiscalYear,
  currency,
}: {
  value: number | null;
  reported: number | null;
  reportedCurrency: string | null;
  fiscalYear: number | null;
  currency: string;
}) {
  const text = formatNumber(value);
  if (reported == null || reportedCurrency === currency) {
    return <TableCell align="right">{text}</TableCell>;
  }
  const year = fiscalYear ? ` (FY${fiscalYear})` : "";
  return (
    <TableCell align="right">
      <Tooltip title={`Reported ${formatNumber(reported)} ${reportedCurrency ?? ""}${year}`}>
        <span>{text}</span>
      </Tooltip>
    </TableCell>
  );
}

interface CompaniesTableProps {
  items: MasterCompanyListItem[];
  selected: Set<string>;
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (id: string, checked: boolean) => void;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (column: string) => void;
  onRowClick: (id: string) => void;
  /** Currency the money columns are in. */
  currency: string;
  loading?: boolean;
  /** Filters are narrowing the list (for the empty message). */
  filtered?: boolean;
  /** The search failed (its error toasted): no "nothing found" row. */
  failed?: boolean;
  /** Under the rows: the pagination. */
  footer?: ReactNode;
  /** Above the rows: the filter bar. */
  toolbar?: ReactNode;
}

export default function CompaniesTable({
  items,
  selected,
  onToggleAll,
  onToggleRow,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  failed = false,
  currency,
  loading = false,
  filtered = false,
  footer,
  toolbar,
}: CompaniesTableProps) {
  const columns = COLUMNS.map((col) => ({
    id: col.id,
    label: col.money ? `${col.label} (${currency})` : col.label,
    align: col.align,
  }));
  return (
    <TableCard loading={loading} footer={footer} toolbar={toolbar}>
      <ScrollTable>
        <SortableTableHead
          columns={columns}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
          defaultDir="asc"
          leading={
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selected.size > 0 && selected.size < items.length}
                checked={items.length > 0 && items.every((c) => selected.has(c.id))}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </TableCell>
          }
        />
        <TableBody>
          {items.map((company) => (
            <TableRow
              key={company.id}
              hover
              sx={{ cursor: "pointer" }}
              selected={selected.has(company.id)}
              onClick={() => onRowClick(company.id)}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.has(company.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggleRow(company.id, e.target.checked)}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {company.name}
                </Typography>
              </TableCell>
              <TableCell>
                <CountryName code={company.country} />
              </TableCell>
              <TableCell>{company.org_number ?? "-"}</TableCell>
              <TableCell>
                {company.ai_industries?.length
                  ? company.ai_industries.join(", ")
                  : (company.industry ?? "-")}
              </TableCell>
              <TableCell>{company.org_form ?? "-"}</TableCell>
              <TableCell align="right">
                {company.employee_size_bucket
                  ? formatBucketLabel(company.employee_size_bucket)
                  : "-"}
              </TableCell>
              <MoneyCell
                value={company.revenue}
                reported={company.reported_revenue}
                reportedCurrency={company.reported_currency}
                fiscalYear={company.fiscal_year}
                currency={currency}
              />
              <MoneyCell
                value={company.operating_result}
                reported={company.reported_operating_result}
                reportedCurrency={company.reported_currency}
                fiscalYear={company.fiscal_year}
                currency={currency}
              />
            </TableRow>
          ))}
          {items.length === 0 && !failed && (
            <EmptyTableRow colSpan={COLUMNS.length + 1}>
              {filtered ? "No companies match your filters" : "No companies found"}
            </EmptyTableRow>
          )}
        </TableBody>
      </ScrollTable>
    </TableCard>
  );
}
